import {
    AbstractedTariffSimOnlyResponse,
    AbstractedTariffWithHardwareResponse,
    GladosEndpointVersion,
    GladosServiceFactory,
    TariffSimOnlyParamsV1,
    TariffWithHardwareParamsV1,
} from '@vfde-sails/glados-v2';
import {
    createApi,
    fakeBaseQuery,
} from '@reduxjs/toolkit/query';
import { getQueryParam } from '@vfde-sails/utils';
import {
    API_BTX_BNT,
    MAP_SALESCHANNEL_TO_API_SALESCHANNEL,
    RedTariff,
    YoungTariff,
} from '@vfde-sails/constants';
import { getAllDiscounts } from '@vfde-sails/page-options';
import {
    selectAvailableSubscriptionIds,
    selectCustomerConfig,
    selectPreviousHardwareConfig,
} from 'Container/App/slice';
import { RootState } from '../../../store';
import { isAxiosError } from 'axios';
import { RequestError } from '@vfde-sails/types';

const createRequestError = (error: unknown): RequestError => {
    if (isAxiosError(error)) {
        return {
            code: error.code,
            status: error.response?.status,
            message: error.message,
        };
    }

    return {
        message: error instanceof Error ? error.message : `${error}`,
    };
};

const gladosApi = createApi({
    reducerPath: 'GladosService',
    baseQuery: fakeBaseQuery<RequestError>(),
    endpoints: build => ({
        getSimOnly: build.query<AbstractedTariffSimOnlyResponse<RedTariff | YoungTariff>, boolean>({
            queryFn: async (isRestlaufzeit, { getState }) => {
                const tariffService = GladosServiceFactory.getTariffService();



                const customerConfig = selectCustomerConfig(getState() as RootState);
                const availableSubscriptionIds = selectAvailableSubscriptionIds(getState() as RootState);

                const {
                    salesChannel,
                    hasGigakombi: isGigakombi,
                } = customerConfig;

                const params: TariffSimOnlyParamsV1 = {
                    btx: API_BTX_BNT,
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel]],
                    discountIds: getAllDiscounts({
                        subscriptionIds: availableSubscriptionIds,
                        isGigakombi,
                        salesChannel,
                        deviceId: null,
                        isTradeIn: false, // In simOnly, this would always be false
                        isTauschbonus: false, // In simOnly, this would always be false
                        isRestlaufzeit,
                    }),
                    tariffIds: availableSubscriptionIds,
                };

                try {
                    const response = await tariffService.getSimOnly<GladosEndpointVersion.V1, RedTariff | YoungTariff>(
                        GladosEndpointVersion.V1,
                        params,
                    );

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
        getTariffWithHardware: build.query<AbstractedTariffWithHardwareResponse<RedTariff | YoungTariff>, {
            atomicId: string;
            isRestlaufzeit: boolean;
        }>({
            queryFn: async (config, { getState }) => {
                const { isRestlaufzeit, atomicId } = config;
                const tariffService = GladosServiceFactory.getTariffService();



                const customerConfig = selectCustomerConfig(getState() as RootState);
                const availableSubscriptionIds = selectAvailableSubscriptionIds(getState() as RootState);
                const previousHardwareConfig = selectPreviousHardwareConfig(getState() as RootState);

                const isTauschbonus = previousHardwareConfig?.hasTauschbonus ?? customerConfig.hasTauschbonus;
                const isTradeIn = previousHardwareConfig?.hasTradeIn ?? customerConfig.hasTradeIn;
                const deviceId = previousHardwareConfig?.deviceId ?? customerConfig.deviceId;

                const {
                    salesChannel,
                    hasGigakombi: isGigakombi,
                } = customerConfig;

                const params: TariffWithHardwareParamsV1 = {
                    btx: API_BTX_BNT,
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel]],
                    discountIds: getAllDiscounts({
                        subscriptionIds: availableSubscriptionIds,
                        isGigakombi,
                        salesChannel,
                        deviceId,
                        isTradeIn,
                        isTauschbonus,
                        isRestlaufzeit,
                    }),
                    atomicIds: [atomicId],
                    tariffIds: availableSubscriptionIds,
                };

                try {
                    const response = await tariffService.getTariffWithHardware<GladosEndpointVersion.V1, RedTariff | YoungTariff>(
                        GladosEndpointVersion.V1,
                        params,
                    );

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
    }),
});

export default gladosApi;

export const {
    getSimOnly,
    getTariffWithHardware,
} = gladosApi.endpoints;
